import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import middleware from "../../Api/Middleware";
import { getCountryName } from '../../../utils';
import ToastSuccess from '../../Toasts/ToastSuccess';
import { ButtonBlue, ButtonDarkBlue } from '../../HouseStyle/Components/Buttons';

const PriceSettingsModal = ({ setPriceModalVisible,refetch, priceModalVisible, setSelectedTax, product, edit, onTaxSettingsChangeCallback, setIsLoading }) => {
    const [taxData, setTaxData] = useState(null);
    const [globalTaxClass, setGlobalTaxClass] = useState(null);
    const [countryOverrides, setCountryOverrides] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchTaxRates = async () => {
        try {
            const response = await middleware.get(`products/taxRates`, {
                params: { product_id: product?.product_id }
            });

            setTaxData(response.data);
            setGlobalTaxClass(response.data.default_tax_class);
            setSelectedTax(Number(response.data.current_tax_rate));

            const overrides = {};
            // Set up country overrides if editing
            if (response.data.tax_rates_by_country) {
                response.data.tax_rates_by_country.forEach(country => {
                    if (country.selected_class) {
                        overrides[country.country_code_id] = country.selected_class;
                    }
                });
                setCountryOverrides(overrides);
            }

            onTaxSettingsChangeCallback({
                currentTaxRate: Number(response.data.current_tax_rate),
                globalTaxClass: response.data.default_tax_class,
                countryOverrides: overrides,
            });
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTaxRates();
    }, [refetch]);

    const handleGlobalTaxClassChange = (newClass) => {
        setGlobalTaxClass(newClass);
    };

    const handleCountryTaxChange = (countryCodeId, taxClass) => {
        if (taxClass === globalTaxClass) {
            // Remove override if it matches global
            const newOverrides = { ...countryOverrides };
            delete newOverrides[countryCodeId];
            setCountryOverrides(newOverrides);
        } else {
            // Set override
            setCountryOverrides(prev => ({
                ...prev,
                [countryCodeId]: taxClass
            }));
        }
    };

    const getSelectedTaxInfo = (country) => {
        const selectedClass = countryOverrides[country.country_code_id] || globalTaxClass;
        return country.rates[selectedClass] || country.rates.high;
    };

    const handleSave = async () => {
        const existingOverride = countryOverrides[taxData.priority_country_id];
        const effectiveClass = existingOverride || globalTaxClass;
        
        const currentTaxRate = taxData.tax_rates_by_country.find(country => 
            country.country_code_id === taxData.priority_country_id
        )?.rates[effectiveClass]?.percentage || taxData?.current_tax_rate;

        onTaxSettingsChangeCallback({
            currentTaxRate,
            globalTaxClass,
            countryOverrides
        });
        
        if (!edit) setPriceModalVisible(false);

        if (!product?.product_id) return;

        try {
            setSaving(true);

            // Format overrides for API
            const formattedOverrides = Object.entries(countryOverrides).map(([countryCodeId, taxClass]) => ({
                country_code_id: parseInt(countryCodeId),
                tax_class: taxClass
            }));

            await middleware.post('products/updateTaxRates', {
                product_id: product.product_id,
                global_tax_class: globalTaxClass,
                country_overrides: formattedOverrides
            });

            ToastSuccess("Product is bijgewerkt", "productUpdate", 2000);

            setPriceModalVisible(false);
        } catch (error) {
            console.error('Error saving tax rates:', error);
            alert('Error saving tax rates. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Helper functie om de class naam te bepalen
    const getTaxClassName = (taxClass) => {
        if (taxClass === 'high') return 'Hoog';
        if (taxClass === 'zero') return 'Geen BTW';
        if (taxClass.startsWith('reduced')) return 'Laag';
        return taxClass;
    };

    return (
        <Modal
            id="modalBlueprint"
            show={priceModalVisible}
            onHide={() => setPriceModalVisible(false)}
            size="lg"
            centered
        >
            <Modal.Header className="modalTop justify-content-between">
                <h4 className='ms-md-2 fw-bold'>BTW-instellingen</h4>
                <button variant="transparent" className='btn' onClick={() => setPriceModalVisible(false)}>
                    <FontAwesomeIcon icon={faXmark} className="closeModal" />
                </button>
            </Modal.Header>
            <Modal.Body className="modalCenter">
                <div className="contendModal w-100">
                    {/* Global Tax Class Selection */}
                    <div className="mb-4 p-3 bg-light rounded">
                        <h5 className="mb-3">Standaard BTW-tarief voor alle landen</h5>
                        <select
                            className="standardInput standardDropdownArrow w-100"
                            value={globalTaxClass}
                            onChange={(e) => handleGlobalTaxClassChange(e.target.value)}
                        >
                            {taxData?.tax_classes?.map(taxClass => (
                                <option key={taxClass.value} value={taxClass.value}>
                                    {taxClass.name}
                                </option>
                            ))}
                        </select>
                        <small className="text-muted mt-2 d-block">
                            Dit tarief wordt toegepast op alle landen, tenzij hieronder een uitzondering wordt ingesteld.
                        </small>
                    </div>

                    <div>
                        <h5 className="mb-3">BTW-tarieven per land</h5>
                        <div className="text-muted mb-3">
                            <small>Selecteer alleen een ander tarief als het afwijkt van het standaard tarief.</small>
                        </div>
                    </div>

                    {/* Country-specific overrides */}
                    <div style={{ maxHeight: '40vh', overflowY: 'scroll' }}>
                        {taxData?.tax_rates_by_country?.map(country => {
                            const selectedInfo = getSelectedTaxInfo(country);
                            const hasOverride = countryOverrides[country.country_code_id] !== undefined;
                            const currentValue = countryOverrides[country.country_code_id] || globalTaxClass;

                            return (
                                <div key={country.country_code_id} className={`mb-3 p-2 ${hasOverride ? 'bg-warning bg-opacity-10 rounded' : ''}`}>
                                    <label className='fw-bold d-flex justify-content-between align-items-center'>
                                        <span>BTW {getCountryName(country.country_code)}</span>
                                        {hasOverride && <span className="badge bg-warning text-dark">Aangepast</span>}
                                    </label>
                                    <div className="d-flex align-items-center gap-2">
                                        <select
                                            className="standardInput standardDropdownArrow flex-grow-1"
                                            value={currentValue}
                                            onChange={(e) => handleCountryTaxChange(country.country_code_id, e.target.value)}
                                        >
                                            {/* Groepeer opties per type */}
                                            <optgroup label={getTaxClassName('high')}>
                                                {country.rates.high && (
                                                    <option value="high">
                                                        {country.rates.high.percentage}%
                                                    </option>
                                                )}
                                            </optgroup>
                                            
                                            <optgroup label={getTaxClassName('reduced')}>
                                                {['reduced', 'reduced2', 'reduced3'].map(key => {
                                                    const rate = country.rates[key];
                                                    if (rate) {
                                                        return (
                                                            <option key={key} value={key}>
                                                                {rate.percentage}%
                                                            </option>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </optgroup>
                                            
                                            <optgroup label={getTaxClassName('zero')}>
                                                {country.rates.zero && (
                                                    <option value="zero">
                                                        {country.rates.zero.percentage}%
                                                    </option>
                                                )}
                                            </optgroup>
                                        </select>
                                        {hasOverride && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => handleCountryTaxChange(country.country_code_id, globalTaxClass)}
                                                title="Reset naar standaard"
                                            >
                                                ↺
                                            </button>
                                        )}
                                    </div>
                                    <small className="text-muted">
                                        Huidig tarief: {selectedInfo.percentage}%
                                    </small>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="modalBottom w-100 d-flex justify-content-between">
                <ButtonDarkBlue
                    text={"Annuleren"}
                    onClickFunction={() => {
                        setPriceModalVisible(false);
                        fetchTaxRates();
                    }}
                />
                <ButtonBlue
                    text={saving ? 'Opslaan...' : 'Opslaan'}
                    onClickFunction={handleSave}
                    disabled={saving}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default PriceSettingsModal;